// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import React, { useState, useEffect } from 'react';
import { useLoggedInState } from '../../context/LoggedInContext';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '../../lib/base';
import * as routes from '../../routes';
import ExitDialogButton from '../common/button/ExitDialogButton';

const lastSeenBlogUpsellKey = 'lastSeenBlogUpsell';
const messageIndexKey = 'blogUpsellMessageIndex';
const upsellMessages = new Map([
  [
    0,
    {
      title: 'Try Microgreen Manager!',
      body: 'Tired of spreadsheets and sticky notes? Try our professional microgreen farm management software, Microgreen Manager.',
    },
  ],
  [
    1,
    {
      title: 'Boost Your Productivity!',
      body: 'Manage your microgreen farm efficiently with our all-in-one software solution, Microgreen Manager.',
    },
  ],
  [
    2,
    {
      title: 'Simplify Farm Management!',
      body: 'Say goodbye to manual tracking and hello to automation with Microgreen Manager.',
    },
  ],
]);

export const UpsellDialog = ({ delaySeconds = 8 }: { readonly delaySeconds?: number }) => {
  const { isLoggedIn } = useLoggedInState();
  const [open, setOpen] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);

  const onClose = () => {
    setOpen(false);
    localStorage?.setItem(lastSeenBlogUpsellKey, new Date().toISOString());
    localStorage?.setItem(messageIndexKey, ((messageIndex + 1) % upsellMessages.size).toString());
  };

  useEffect(() => {
    const lastSeenUpsell = localStorage?.getItem(lastSeenBlogUpsellKey);
    const now = new Date();
    const lastSeenDate = lastSeenUpsell ? new Date(lastSeenUpsell) : null;
    const is24HoursPassed = lastSeenDate ? now.getTime() - lastSeenDate.getTime() > 24 * 60 * 60 * 1000 : true;

    const storedMessageIndex = Number.parseInt(localStorage?.getItem(messageIndexKey) || '0') || 0;

    const showAfterDelay = () => {
      if (is24HoursPassed && !isLoggedIn) {
        setMessageIndex(storedMessageIndex % upsellMessages.size);
        setOpen(true);
        localStorage?.setItem(lastSeenBlogUpsellKey, now.toISOString());
      }
    };

    const timer = setTimeout(showAfterDelay, delaySeconds * 1000);

    return () => clearTimeout(timer);
  }, [isLoggedIn, delaySeconds]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start' }}
      >
        <ExitDialogButton onCloseDialog={onClose} />
        <Typography variant="h4" sx={{ textAlign: 'center' }}>
          {upsellMessages.get(messageIndex)?.title ?? 'Try Microgreen Manager!'}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>
        <Typography variant="body1" sx={{ mb: 1, textAlign: 'center' }}>
          {upsellMessages.get(messageIndex)?.body ??
            'Tired of spreadsheets and sticky notes? Try our professional microgreen farm management software, Microgreen Manager.'}
        </Typography>
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button sx={{ width: { xs: '100%', md: '50%' } }} variant="outlined" onClick={onClose}>
          Maybe Later
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={() => {
            onClose();
            window.open(`${window.location.origin}${routes.SIGNUP}`, '_blank');
          }}
          type="submit"
        >
          Try It Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};
